import * as React from 'react';
import { ProtectedPageWrapper } from '../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { buildTranslator, CustomFormActivityListing, DateBuilder, rng } from '../../amorg-common';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { H2 } from '../../designSystem/typography/Heading';
import { Card } from '../../designSystem/Card';
import { createCustomForm, getCustomFormActivityList, getCustomFormTemplates } from '../../api/customFormClientApi';
import { Modal } from '../../designSystem/Modal';
import { Select } from '../../designSystem/inputs/Select';
import { CustomFormTemplate } from '../../amorg-common/DTOs/CustomFormTemplate';
import { Button } from '../../designSystem/button/Button';
import { GenericActivityListingView } from '../../features/activities/common/GenericActivityListingView';
import { AmorgMenu, AmorgMenuContent } from '../../designSystem/amorgMenu';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { isSameDate } from '../../designSystem/formField/DateStartEndTimeFields';
import { P } from '../../designSystem/typography/Body';
import { toTextDateString, toTimeString } from '../../utils/date';
import { Redirect } from 'react-router-dom';
import { createActivity } from '../../api/activityClientApi';
import { ButtonRow } from '../../designSystem/button/ButtonRow';
import { CardButton } from '../../designSystem/CardButton';

export function ErgonomicsPage():JSX.Element
{
    const [state, setState] = React.useState<{
        isLoading:boolean, 
        templates:CustomFormTemplate[], 
        activities:CustomFormActivityListing[]
        selectedTemplateId:string,
        showModal:boolean,
        redirectId:string
    }>(
        { isLoading:true, templates:[], activities:[], selectedTemplateId:'', showModal:false, redirectId:'' }
    );

    React.useEffect(()=>{
        const getData = async () => {
            const templates = await getCustomFormTemplates();
            const activities = await getCustomFormActivityList();
            setState({ isLoading:false, activities, templates, selectedTemplateId:'', showModal:false, redirectId:'' });
        }
        getData();
    }, []);

    async function handleClickCreate()
    {
        const activityId = rng.getRandomId();
        const formId = rng.getRandomId();
        const template = state.templates.find(t => t.templateId === state.selectedTemplateId);

        const activityResult = await createActivity(
            activityId, 
            'CustomForm',
            'Ny undersøgelse',
            translator(template.description),
            DateBuilder.now().date, 
            DateBuilder.now().addMonths(3).date, 
            '');
        if(activityResult.isSuccess)
        {
            const formResult = await createCustomForm(formId, activityId, state.selectedTemplateId);
            if(formResult.isSuccess)
            {
                setState({...state, redirectId:activityId});
            }
        }
    }

    const translator = buildTranslator('da');
    

    return (
        
        <ProtectedPageWrapper requiredPermissions={['ViewCustomFormAnswerList']} selectedMenuItemKey='ergonomics'>
            <SingleColumnLayout  
                noDivider
                contentContainer={
                    <>
                    {state.redirectId !== '' &&
                        <Redirect to={`/customforms/${state.redirectId}`} />
                    }

                    <LoadingPageOverlay loading={state.isLoading} />

                    <GenericActivityListingView
                        headerIcon='physical_therapy'
                        headerText="Aktuelle Ergonomiske undersøgelser"
                        pastHeaderText="TIDLIGERE undersøgelser"
                        newActivityButtonText="Opret ny undersøgelse"
                        activities={state.activities}
                        onClick={(id) => setState({...state, redirectId:id})}
                        onClickNew={() => setState({...state, showModal:true})}                        
                    />

                    <Modal title={'Opret ny undersøgelse'} 
                        showModal={state.showModal}
                        onCancel={() => setState({...state, showModal:false})}
                        content={
                            <Flex flexDirection="column" gap={24}>
                                <Select 
                                    selectedValue={state.selectedTemplateId} 
                                    options={
                                        [
                                            { value:'', text:'Vælg skabelon' },
                                            ...state.templates.map(t => ({ value:t.templateId, text:translator(t.name) }))
                                        ]
                                    } 
                                    onChange={function (newValue: string): void
                                    {
                                        setState({ ...state, selectedTemplateId:newValue });
                                    }}
                                />

                                
                                {state.selectedTemplateId==='' 
                                    ?   <P>Vælg en skabelon for at fortsætte</P> 
                                    :   <Card>
                                            {translator(state.templates.find(t => t.templateId === state.selectedTemplateId)?.description)}
                                        </Card>
                                }

                                <ButtonRow>
                                    <Button 
                                        variant='filled' 
                                        text="Opret" 
                                        disabled={state.selectedTemplateId===''}
                                        onClick={() => handleClickCreate() }
                                    />
                                </ButtonRow>
                            </Flex>
                        } 
                    />
                    </>
                } 
            />
            
        </ProtectedPageWrapper>
    );
}

type CustomFormActivityViewProps = {
    listing:CustomFormActivityListing
}

export function CustomFormActivityView(props:CustomFormActivityViewProps):JSX.Element
{
    const content:AmorgMenuContent[] = [];
    const now = new Date();

    content.push(
    {
        type:'leadingContent',
        styleVariant: 'default',
        leadingContent:{ type:'none' },
        primaryContent:{ type:'text', text:props.listing.formName}
    });

    if(now < props.listing.start)
    {
        content.push(
        {
            type:'leadingContent',
            styleVariant: 'default',
            leadingContent:{ type:'icon', iconName:'actions_unavailable' },
            primaryContent:{ type:'text', text:'Undersøgelsen er endnu ikke startet'}
        });
    }
    else if(props.listing.start < now && now < props.listing.end)
    {
        content.push(
        {
            type:'leadingContent',
            styleVariant: 'default',
            leadingContent:{ type:'icon', iconName:'actions_done' },
            primaryContent:{ type:'text', text:'Undersøgelsen er igangværende'}
        });
    }
    else
    {
        content.push(
        {
            type:'leadingContent',
            styleVariant: 'default',
            leadingContent:{ type:'icon', iconName:'actions_unavailable' },
            primaryContent:{ type:'text', text:'Undersøgelsen er afsluttet'}
        });
    }

    content.push(
        {
            type:'leadingContent',
            styleVariant: 'default',
            leadingContent:{ type:'icon', iconName:'interface_user_team' },
            primaryContent:{ type:'text', text:`Antal besvarelser: ${props.listing.answerCount}` }
        }
    )

    function handleOnClick(){
        document.location=`/customforms/${props.listing.id}`;
    }


    return (
        
        <CardButton onClick={ handleOnClick }>
            {/* <div>
                <PrettyPrintJson data={props.listing} />
            </div> */}
            <Flex flexDirection="column" gap={24}>
                <Flex flexDirection="column">
                    <H2>{props.listing.activityTitle}</H2>
                    <P>
                    {!isSameDate(props.listing.start, props.listing.end) 
                        ? toTextDateString(props.listing.start) + " kl. " + toTimeString(props.listing.start) + " - " + toTextDateString(props.listing.end) + " kl. " + toTimeString(props.listing.end) 
                        : toTextDateString(props.listing.start) + " kl. " + toTimeString(props.listing.start) + " - " + toTimeString(props.listing.end)
                    }
                    </P>
                </Flex>
                <AmorgMenu content={content} />
            </Flex>
        </CardButton>
    );
}